import { useRef, useState, type RefObject } from 'react';
import {
  diffCharts,
  rectifyBirthTime,
} from '@astrally/astro-engine';
import type { LifeEvent, LifeEventType, RectificationResult } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { ScreenHeader, Section } from '../../components/common';

const EVENT_TYPES: { value: LifeEventType; label: string }[] = [
  { value: 'major-move', label: 'Major move' },
  { value: 'first-job', label: 'First job' },
  { value: 'career-shift', label: 'Major career shift' },
  { value: 'marriage', label: 'Marriage / significant relationship' },
  { value: 'relationship-start', label: 'Relationship start' },
  { value: 'separation', label: 'Separation' },
  { value: 'education-milestone', label: 'Education milestone' },
  { value: 'sibling-event', label: 'Sibling event' },
  { value: 'family-event', label: 'Major family event' },
  { value: 'accident-or-surgery', label: 'Accident or surgery' },
  { value: 'business-launch', label: 'Business launch' },
];

const NO_PROFILE = 'Your birth details have not loaded yet — reopen this screen in a moment.';

/** Strip SDK prefixes so a failure reads as a sentence, not a stack trace. */
const reasonOf = (e: unknown) => String(e).replace(/^(FirebaseError|Error):\s*/, '').slice(0, 200);

/**
 * Guided birth-time refinement. Scores candidate windows against dated life
 * events. Always presented as an estimate; the user can accept a window,
 * reject it, add events, stay in unknown mode, or enter an exact time later.
 */
export function RefinementScreen() {
  const { t } = useI18n();
  const app = useApp();
  const [events, setEvents] = useState<LifeEvent[]>([]);
  const [type, setType] = useState<LifeEventType>('major-move');
  const [date, setDate] = useState('');
  const [result, setResult] = useState<RectificationResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [changes, setChanges] = useState<string[] | null>(null);
  const [exactEntry, setExactEntry] = useState('');
  const [error, setError] = useState('');
  // Which apply is in flight ('exact' or a window key), so only that button spins.
  const [applying, setApplying] = useState<string | null>(null);
  const [applyNote, setApplyNote] = useState<{ ok: boolean; text: string } | null>(null);
  const eventsRef = useRef<HTMLDivElement>(null);
  const applyRef = useRef<HTMLDivElement>(null);

  // Guarded: jsdom has no scrollIntoView, and it is a nicety — the message is
  // what actually tells someone what happened.
  const reveal = (ref: RefObject<HTMLDivElement>) =>
    ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });

  const addEvent = () => {
    if (!date) {
      setError('Pick the date this happened first — an undated event cannot be scored.');
      return;
    }
    setError('');
    setEvents([...events, { type, dateISO: date }]);
    setDate('');
  };

  /**
   * The CTA stays pressable with no events, per SocialOptInScreen: a disabled
   * primary button reads as a broken app rather than an unfinished form.
   */
  const run = async () => {
    if (events.length === 0) {
      setError(t('form.needEvent'));
      reveal(eventsRef);
      return;
    }
    if (!app.birthProfile) {
      setError(NO_PROFILE);
      return;
    }
    setBusy(true);
    setError('');
    setResult(null);
    try {
      // Rectification samples many candidate charts — run off the UI tick.
      await new Promise((r) => setTimeout(r, 30));
      setResult(rectifyBirthTime(app.birthProfile.input, events, { stepMinutes: 20 }));
    } catch (e) {
      setError(`Scoring failed, so your birth time is unchanged. ${reasonOf(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const applyWindow = async (startLocal: string, endLocal: string, key: string) => {
    if (!app.birthProfile) {
      setApplyNote({ ok: false, text: NO_PROFILE });
      reveal(applyRef);
      return;
    }
    const parse = (s: string) => {
      const [h, m] = s.split(':').map(Number);
      return { hour: h, minute: m };
    };
    setApplying(key);
    setApplyNote(null);
    try {
      const before = app.chart;
      const after = await app.recalcChart({
        ...app.birthProfile.input,
        timeInput: {
          kind: 'approximate',
          window: { start: parse(startLocal), end: parse(endLocal) },
          label: 'custom',
        },
      });
      if (!after) {
        setApplyNote({ ok: false, text: `Nothing changed — ${NO_PROFILE}` });
        return;
      }
      setChanges(before ? diffCharts(before, after) : null);
      setApplyNote({ ok: true, text: `Your birth time is now the ${startLocal}–${endLocal} window.` });
    } catch (e) {
      setApplyNote({ ok: false, text: `Nothing changed — your birth time is as it was. ${reasonOf(e)}` });
    } finally {
      setApplying(null);
      reveal(applyRef);
    }
  };

  const applyExact = async () => {
    if (!/^\d{2}:\d{2}$/.test(exactEntry)) {
      setApplyNote({ ok: false, text: 'Enter the recorded time as HH:MM first.' });
      reveal(applyRef);
      return;
    }
    if (!app.birthProfile) {
      setApplyNote({ ok: false, text: NO_PROFILE });
      reveal(applyRef);
      return;
    }
    const [h, m] = exactEntry.split(':').map(Number);
    setApplying('exact');
    setApplyNote(null);
    try {
      const before = app.chart;
      const after = await app.recalcChart({
        ...app.birthProfile.input,
        timeInput: { kind: 'exact', time: { hour: h, minute: m } },
      });
      if (!after) {
        setApplyNote({ ok: false, text: `Nothing changed — ${NO_PROFILE}` });
        return;
      }
      setChanges(before ? diffCharts(before, after) : null);
      setApplyNote({ ok: true, text: `Your birth time is now exactly ${exactEntry}.` });
    } catch (e) {
      setApplyNote({ ok: false, text: `Nothing changed — your birth time is as it was. ${reasonOf(e)}` });
    } finally {
      setApplying(null);
      reveal(applyRef);
    }
  };

  return (
    <div className="screen">
      <ScreenHeader title={t('refine.title')} back />
      <div className="muted small" style={{ marginBottom: 14, lineHeight: 1.55 }}>{t('refine.body')}</div>

      <Section title="Dated life events">
        <div className="card col" ref={eventsRef}>
          {events.map((e, i) => {
            const label = EVENT_TYPES.find((x) => x.value === e.type)?.label ?? e.type;
            return (
              <div key={i} className="spread small">
                <span>{label}</span>
                <span className="row">
                  <span className="muted">{e.dateISO}</span>
                  <button
                    className="chip"
                    aria-label={`Remove ${label} on ${e.dateISO}`}
                    onClick={() => setEvents(events.filter((_, j) => j !== i))}
                  >
                    <span aria-hidden>✕</span>
                  </button>
                </span>
              </div>
            );
          })}
          <div className="row">
            <select className="select" value={type} onChange={(e) => setType(e.target.value as LifeEventType)}>
              {EVENT_TYPES.map((et) => (
                <option key={et.value} value={et.value}>{et.label}</option>
              ))}
            </select>
          </div>
          <div className="row">
            <input
              className="input"
              type="date"
              value={date}
              onChange={(e) => {
                setError('');
                setDate(e.target.value);
              }}
            />
            <button className={`btn btn--ghost ${date ? '' : 'btn--incomplete'}`} onClick={addEvent}>
              {t('refine.addEvent')}
            </button>
          </div>
          <div className="tiny faint">
            {events.length === 0
              ? 'At least one dated event is required — that is what the scoring compares.'
              : `${events.length} event${events.length === 1 ? '' : 's'} added. More events narrow the window further.`}
          </div>
          {error && <div className="banner banner--danger small" role="alert">{error}</div>}
          <button
            className={`btn btn--primary btn--block ${events.length === 0 ? 'btn--incomplete' : ''}`}
            disabled={busy}
            onClick={run}
          >
            {busy ? <span className="spinner" /> : t('refine.run')}
          </button>
        </div>
      </Section>

      {result && (
        <Section title="Suggested windows">
          <div className="banner banner--gold small" style={{ marginBottom: 10 }}>
            {t('refine.disclaimer')}
          </div>
          {result.windows.length === 0 && (
            <div className="card muted small">
              No window stood out from these events yet — add more dated events, or continue in your
              current mode. That is a completely valid choice.
            </div>
          )}
          <div className="col">
            {result.windows.map((w, i) => (
              <div key={i} className="card">
                <div className="spread" style={{ marginBottom: 6 }}>
                  <span className="h3">{w.startLocal} – {w.endLocal}</span>
                  <span className="gold small">fit {(w.score * 100).toFixed(0)}%</span>
                </div>
                <div className="col" style={{ gap: 3, marginBottom: 10 }}>
                  {w.explanation.map((e, j) => (
                    <div key={j} className="tiny muted">• {e}</div>
                  ))}
                </div>
                <div className="row">
                  <button
                    className="btn btn--primary btn--small"
                    disabled={applying !== null}
                    onClick={() => applyWindow(w.startLocal, w.endLocal, `w${i}`)}
                  >
                    {applying === `w${i}` ? <span className="spinner" /> : t('refine.accept')}
                  </button>
                  <button className="btn btn--ghost btn--small" onClick={() => setResult(null)}>
                    {t('refine.reject')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Found an exact recorded time?">
        <div className="card row">
          <input
            className="input"
            type="time"
            value={exactEntry}
            onChange={(e) => {
              setApplyNote(null);
              setExactEntry(e.target.value);
            }}
          />
          <button
            className={`btn btn--primary ${exactEntry ? '' : 'btn--incomplete'}`}
            disabled={applying !== null}
            onClick={applyExact}
          >
            {applying === 'exact' ? <span className="spinner" /> : 'Apply'}
          </button>
        </div>
      </Section>

      {/* One feedback slot for both apply paths — they end in the same place,
          and each scrolls here so the outcome is never silent. */}
      <div ref={applyRef}>
        {applyNote && (
          <div
            className={`banner small ${applyNote.ok ? '' : 'banner--danger'}`}
            role={applyNote.ok ? 'status' : 'alert'}
            style={
              applyNote.ok
                ? { marginTop: 12, background: 'var(--ok-soft)', color: 'var(--ok)' }
                : { marginTop: 12 }
            }
          >
            {applyNote.text}
          </div>
        )}
      </div>

      {changes && (
        <Section title={t('refine.changes')}>
          <div className="card col" style={{ gap: 6 }}>
            {changes.map((c, i) => (
              <div key={i} className="small">• {c}</div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
